import React, { useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useGetStepQueryParams } from "features/bikeLabelOrder/hooks/useGetStepQueryParams"
import SwitchBikeLabelOrderStep from "./Partials/SwitchBikeLabelOrderStep"
import { LimitStepEnum, QUERY_LABEL_ORDER_STEP } from "features/bikeLabelOrder/constants"
import { BikeLabelOrderIndexStepEnum } from "features/bikeLabelOrder/types"
import { handlerGenerateByEnumeration } from "features/bikeLabelOrder/utils"
import { useBicyCodeBilingOperator } from "../hooks/useGetBicyOperator"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { handleClearOrderState } from "features/bikeLabelOrder/slice/bikeLabelOrderSlice"
import { useAsideMenu } from "hooks"
import { LABEL_ORDER_ID } from "utils/constants/menuTypes"

type BikeLabelOrderStepContextValue = {
	handleNextBikeLabelOrderStep: VoidFunction
	checkBillingConsistency: () => boolean
	entityStoreChains: string
	updateRequestBilingInformation: () => Promise<any>
	step: `${BikeLabelOrderIndexStepEnum}`
} & ReturnType<typeof handlerGenerateByEnumeration<typeof BikeLabelOrderIndexStepEnum>>

export const BikeLabelOrderStepContext = React.createContext({} as BikeLabelOrderStepContextValue)
export const useBikeLabelOrderStep = () => {
	const context = React.useContext(BikeLabelOrderStepContext)
	if (context === undefined) {
		//Only for dev
		throw new Error("useBikeLabelOrderStep must be used within a BikeLabelOrderStep")
	}
	return context
}

type BikeLabelOrderStepProps = {
	[key: string]: unknown
}

const BikeLabelOrderStep: React.FC<BikeLabelOrderStepProps> = (props) => {
	const step = useGetStepQueryParams()
	const location = useLocation()
	const { setActiveAsideMenu, mainMenuCollection, findIndexMenu } = useAsideMenu()
	const dispatch = useAppDispatch()
	const validatedLabelOrderStep = useAppSelector(
		(state) => state.bikeLabelOrder.validatedLabelOrderStep
	)
	const isOrderFinished = useAppSelector((state) => state.bikeLabelOrder.finishOrder)

	const navigate = useNavigate()
	const { checkBillingConsistency, updateRequestBilingInformation, entityStoreChains } =
		useBicyCodeBilingOperator()

	//Function for navigate to next step
	const handleNextBikeLabelOrderStep = () => {
		navigate({
			pathname: location.pathname,
			search: `?${QUERY_LABEL_ORDER_STEP}=${Math.min(
				Number(step) + 1,
				LimitStepEnum.MAX_LIMIT_STEP
			)}`,
		})
	}

	useEffect(() => {
		setActiveAsideMenu(mainMenuCollection[findIndexMenu(LABEL_ORDER_ID)].id)
	}, [])

	//Redirect user to last step validate
	useEffect(() => {
		if (Number(validatedLabelOrderStep + 1) < Number(step)) {
			navigateToStep(validatedLabelOrderStep)
		}
	}, [step, validatedLabelOrderStep])

	//Redirect User to First Page when Order is Finish and Reset Order Status
	useEffect(() => {
		if (isOrderFinished && step !== BikeLabelOrderIndexStepEnum.CONFIRMATION_STEP) {
			navigateToStep(BikeLabelOrderIndexStepEnum.COMMAND_STEP)
			dispatch(handleClearOrderState())
		}
	}, [isOrderFinished, step])

	//Function for Navigate to other step
	const navigateToStep = (step) => {
		return navigate({
			pathname: location.pathname,
			search: `?${QUERY_LABEL_ORDER_STEP}=${step}`,
		})
	}

	return (
		<BikeLabelOrderStepContext.Provider
			value={{
				entityStoreChains,
				handleNextBikeLabelOrderStep,
				checkBillingConsistency,
				updateRequestBilingInformation,
				...handlerGenerateByEnumeration<typeof BikeLabelOrderIndexStepEnum>(
					BikeLabelOrderIndexStepEnum,
					navigateToStep
				),
				step,
			}}
		>
			<React.Suspense fallback={null}>
				<SwitchBikeLabelOrderStep step={step} {...props} />
			</React.Suspense>
		</BikeLabelOrderStepContext.Provider>
	)
}
export default BikeLabelOrderStep
